import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Paper,
  Chip,
  ImageList,
  ImageListItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

const getStatusColor = (status) => {
  if (!status) return "default";
  switch (status.toLowerCase()) {
    case "new":
      return "primary";
    case "in_progress":
      return "warning";
    case "resolved":
      return "success";
    default:
      return "default";
  }
};

const capitalizeFirstLetter = (string) => {
  if (!string) return "";
  return string
    .split(/[ _]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const isImageUrl = (url) => {
  if (!url) return false;
  const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp"];
  const lowerUrl = url.toLowerCase();
  return imageExtensions.some((ext) => lowerUrl.includes(ext));
};

const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExtensions = [".mp4", ".mpeg", ".mov", ".avi", ".webm"];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some((ext) => lowerUrl.includes(ext));
};

const AttachmentPreviewDialog = ({ open, onClose, url }) => {
  const isImage = isImageUrl(url);
  const isVideo = isVideoUrl(url);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: "#1A1A1A",
          color: "#FFF",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#FFF",
          borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
        }}
      >
        <Typography variant="h6">Attachment Preview</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "#FFF" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: "#1A1A1A" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 300,
          }}
        >
          {isImage ? (
            <img
              src={url}
              alt="Attachment"
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />
          ) : isVideo ? (
            <video
              controls
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
              }}
            >
              <source src={url} />
              Your browser does not support the video tag.
            </video>
          ) : (
            <Box sx={{ textAlign: "center" }}>
              <AttachFileIcon sx={{ fontSize: 60, color: "#FFD700" }} />
              <Typography
                variant="body2"
                sx={{ mt: 2, color: "rgba(255, 255, 255, 0.7)" }}
              >
                Preview not available for this file type
              </Typography>
              <Typography
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "block",
                  mt: 2,
                  color: "#FFD700",
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                    color: "#FFA000",
                  },
                }}
              >
                Open in new tab
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const TicketDetailsDialog = ({ open, onClose, ticket }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  if (!ticket) return null;

  const hasAttachments =
    ticket.attachmentURL &&
    Array.isArray(ticket.attachmentURL) &&
    ticket.attachmentURL.length > 0;

  const handlePreviewOpen = (url) => {
    setPreviewUrl(url);
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewUrl(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            bgcolor: "#1A1A1A",
            color: "#FFF",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            color: "#FFF",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Ticket Details - {ticket.ticketId || ticket.id}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "#FFF",
              "&:hover": {
                transform: "rotate(90deg)",
                transition: "transform 0.3s",
                bgcolor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            borderColor: "rgba(255, 239, 153, 0.20)",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Ticket Info */}
            <Box
              sx={{
                p: 2,
                bgcolor: "rgba(255, 215, 0, 0.05)",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "rgba(255, 239, 153, 0.20)",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                <Box>
                  <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.8)" }}>
                    <strong style={{ color: "#FFD700" }}>Username:</strong> {ticket.username}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.8)" }}>
                    <strong style={{ color: "#FFD700" }}>Role:</strong> {ticket.role || "N/A"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.8)" }}>
                    <strong style={{ color: "#FFD700" }}>Category:</strong>{" "}
                    {capitalizeFirstLetter(ticket.category)}
                  </Typography>
                </Box>
                <Chip
                  label={capitalizeFirstLetter(ticket.status)}
                  color={getStatusColor(ticket.status)}
                  sx={{ fontWeight: 500 }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.8)" }}>
                <strong style={{ color: "#FFD700" }}>Created:</strong>{" "}
                {new Date(ticket.createdAt).toLocaleString()}
              </Typography>
            </Box>

            {/* Description */}
            <Box>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 600, color: "#FFD700" }}
              >
                Description
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: "rgba(255, 215, 0, 0.05)",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "rgba(255, 239, 153, 0.20)",
                }}
              >
                <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.8)" }}>
                  {ticket.description}
                </Typography>
              </Paper>
            </Box>

            {/* Attachments */}
            {hasAttachments && (
              <Box>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: 600, color: "#2196F3" }}
                >
                  Attachments ({ticket.attachmentURL.length})
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: "rgba(33, 150, 243, 0.05)",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "rgba(33, 150, 243, 0.3)",
                  }}
                >
                  <ImageList
                    sx={{
                      width: "100%",
                      maxHeight: 300,
                    }}
                    cols={3}
                    rowHeight={120}
                    gap={8}
                  >
                    {ticket.attachmentURL.map((url, index) => (
                      <ImageListItem
                        key={index}
                        sx={{
                          cursor: "pointer",
                          border: "1px solid",
                          borderColor: "rgba(255, 239, 153, 0.20)",
                          borderRadius: 1,
                          overflow: "hidden",
                          position: "relative",
                          bgcolor: "rgba(0, 0, 0, 0.3)",
                          "&:hover": {
                            opacity: 0.8,
                            boxShadow: "0 0 10px rgba(255, 215, 0, 0.3)",
                            borderColor: "#FFD700",
                          },
                        }}
                        onClick={() => handlePreviewOpen(url)}
                      >
                        {isImageUrl(url) ? (
                          <img
                            src={url}
                            alt={`Attachment ${index + 1}`}
                            loading="lazy"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        ) : isVideoUrl(url) ? (
                          <Box
                            sx={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "grey.200",
                              position: "relative",
                            }}
                          >
                            <video
                              src={url}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                            <PlayCircleOutlineIcon
                              sx={{
                                position: "absolute",
                                fontSize: 40,
                                color: "white",
                                opacity: 0.9,
                              }}
                            />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "rgba(0, 0, 0, 0.5)",
                            }}
                          >
                            <AttachFileIcon sx={{ fontSize: 40, color: "#FFD700" }} />
                            <Typography
                              variant="caption"
                              sx={{ mt: 1, color: "rgba(255, 255, 255, 0.7)" }}
                            >
                              File {index + 1}
                            </Typography>
                          </Box>
                        )}
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Paper>
              </Box>
            )}

            {/* Remarks */}
            {ticket.remarks && (
              <Box>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: 600, color: "#4CAF50" }}
                >
                  Remarks
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: "rgba(76, 175, 80, 0.05)",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "rgba(76, 175, 80, 0.3)",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.8)" }}>
                    {ticket.remarks}
                  </Typography>
                </Paper>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Attachment Preview Dialog */}
      <AttachmentPreviewDialog
        open={previewOpen}
        onClose={handlePreviewClose}
        url={previewUrl}
      />
    </>
  );
};

export default TicketDetailsDialog;
